import React, { useEffect, useRef, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import useTimeoutEffect from "./useTimeoutEffect";
import { useMeetingStore } from "../../stores/useMeetingStore";
import { useUserStore } from "../../stores/useUserStore";
import useGetMeeting from "../api/useGetMeeting";
import useMeetingChangeSocket from "../api/useMeetingChangeSocket";
import useShowError from "./useShowError";
import { toString } from "../../utilities/stringify";

const useMeetingFromParams = () => {
    const navigate = useNavigate();
    const meetingId = (useParams() as { id: string }).id;

    const { setMeeting, meeting } = useMeetingStore();
    const user = useUserStore((state) => state.user);
    const syncIntervalRef = useRef<NodeJS.Timeout | null>(null);

    useMeetingChangeSocket({ meetingId });
    const { getMeeting, loading, error, clearError } = useGetMeeting();

    useShowError(error, clearError);

    // 👇 新增：判断是否需要定期同步
    // 只在投票进行中时启用定期同步，避免不必要的请求
    const shouldSyncPeriodically = useMemo(() => {
        if (!meeting) return false;

        // ✅ 只在以下情况启用定期同步：
        // 1. 当前正在进行投票轮次
        const isVotingActive = meeting.votingRound !== undefined &&
            meeting.votingRound !== null &&
            meeting.votingRound.length > 0;

        // 2. 当有多个参会人时（说明会议已经开始）
        const hasMultipleParticipants = meeting.participants &&
            meeting.participants.length > 1;

        const result = isVotingActive && hasMultipleParticipants;

        console.log(`shouldSyncPeriodically: ${result} (voting: ${isVotingActive}, participants: ${meeting.participants?.length ?? 0})`);

        return result;
    }, [meeting?.votingRound, meeting?.participants?.length]);

    // 👇 改进：只在需要时启用定期同步
    useEffect(() => {
        if (!meetingId || !shouldSyncPeriodically) {
            // ✅ 如果不需要同步，清除已有的定时器
            if (syncIntervalRef.current) {
                console.log("Stopping periodic sync (not needed)");
                clearInterval(syncIntervalRef.current);
                syncIntervalRef.current = null;
            }
            return;
        }

        const syncMeetingData = async () => {
            try {
                console.log(`[SYNC] Periodic sync: Refreshing meeting ${meetingId}`);
                const latestMeeting = await getMeeting(meetingId);

                if (latestMeeting) {
                    // 👇 只在数据有变化时才更新
                    if (toString(meeting) !== toString(latestMeeting)) {
                        console.log("[SYNC] Meeting data changed, updating store");
                        setMeeting(latestMeeting);
                    }
                }
            } catch (err) {
                console.error("[SYNC] Periodic sync failed:", err);
            }
        };

        // 👇 改进：定期同步的间隔可以更长（10 秒）
        // 因为这只在投票进行时启用，作为 SignalR 的备份方案
        console.log("Starting periodic sync (voting in progress)");
        syncIntervalRef.current = setInterval(syncMeetingData, 10000);

        return () => {
            if (syncIntervalRef.current) {
                clearInterval(syncIntervalRef.current);
                syncIntervalRef.current = null;
            }
        };
    }, [meetingId, getMeeting, meeting, setMeeting, shouldSyncPeriodically]);

    useTimeoutEffect(
        () => {
            if (meetingId.length === 0) {
                console.warn("Meeting ID is empty, navigating to home");
                navigate("/");
            }
            else if (meeting?.id !== meetingId) {
                (async () => {
                    console.log(`Loading meeting: ${meetingId}`);
                    let loadedMeeting = await getMeeting(meetingId);
                    if (!loadedMeeting) {
                        console.warn(`Meeting ${meetingId} not found, navigating to home`);
                        return navigate("/");
                    }
                    console.log(`Meeting ${meetingId} loaded successfully`);
                    setMeeting(loadedMeeting);
                })();
            }
        },
        [user.id],
        1000
    );

    return loading;
};

export default useMeetingFromParams;